import React, { useState } from 'react';
import { Map, Home, Building2, TreePine, Store, ChevronRight } from 'lucide-react';
import { masterplanData } from '../../data/mock';

const zoneIcons = {
  "Zone Villas Premium": Home,
  "Zone Duplex": Building2,
  "Zone Appartements": Building2,
  "Espaces Verts": TreePine,
  "Équipements & Services": Store,
};

const ZoneLegendItem = ({ zone, index, isActive, onMouseEnter, onMouseLeave, onClick }) => {
  const IconComponent = zoneIcons[zone.name] || Home;
  
  return (
    <div
      className={`relative p-4 rounded-xl cursor-pointer transition-all duration-300 overflow-hidden group ${
        isActive 
          ? 'shadow-lg scale-[1.02]' 
          : 'bg-[#FAFAFA] hover:bg-[#F5F5F5] border border-gray-100'
      }`}
      style={{ backgroundColor: isActive ? zone.color : undefined }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      data-testid={`zone-${index}`}
    >
      {/* Animated background bar */}
      <div 
        className={`absolute left-0 top-0 bottom-0 transition-all duration-500 ${isActive ? 'w-full' : 'w-1 group-hover:w-2'}`}
        style={{ backgroundColor: zone.color, opacity: isActive ? 1 : 0.8 }}
      />
      
      <div className="relative z-10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
            isActive ? 'bg-white/20' : 'bg-white'
          }`}>
            <IconComponent className={`w-5 h-5 ${isActive ? 'text-white' : 'text-[#666]'}`} />
          </div>
          <div>
            <p className={`font-medium text-sm ${isActive ? 'text-white' : 'text-[#1a1a1a]'}`}>
              {zone.name}
            </p>
            <p className={`text-xs ${isActive ? 'text-white/70' : 'text-[#999]'}`}>
              {zone.lots}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xl font-light ${isActive ? 'text-white' : 'text-[#1a1a1a]'}`} 
                style={{ fontFamily: 'Playfair Display, serif' }}>
            {zone.percentage}
          </span>
          <ChevronRight className={`w-4 h-4 transition-transform ${isActive ? 'text-white rotate-90' : 'text-[#999]'}`} />
        </div>
      </div>
      
      {/* Expanded details */}
      <div className={`overflow-hidden transition-all duration-300 ${isActive ? 'max-h-24 mt-4' : 'max-h-0'}`}>
        <div className="relative z-10 pt-3 border-t border-white/20">
          <p className="text-white/90 text-sm mb-2">{zone.description}</p>
          <p className="text-white/70 text-xs">Surface: {zone.surface}</p>
        </div>
      </div>
    </div>
  );
};

const Masterplan = () => {
  const [activeZone, setActiveZone] = useState(null);
  const [hoveredZone, setHoveredZone] = useState(null);

  const currentZone = activeZone !== null ? activeZone : hoveredZone;

  return (
    <section className="py-24 lg:py-32 bg-white" data-testid="masterplan-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-[#2D5A27]/10 px-4 py-2 rounded-full mb-6">
            <Map className="w-4 h-4 text-[#2D5A27]" />
            <span className="text-[#2D5A27] text-sm font-medium tracking-wide">Vision Territoriale</span>
          </div>
          <h2 
            className="text-4xl lg:text-5xl text-[#1a1a1a] mb-6 leading-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {masterplanData.title}
          </h2>
          <p className="text-[#666] text-lg leading-relaxed">
            {masterplanData.description}
          </p>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Interactive Zone Legend - Left */}
          <div className="lg:col-span-4 space-y-3">
            <h3 className="text-lg font-medium text-[#1a1a1a] mb-4 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              <span className="w-8 h-8 bg-[#2D5A27]/10 rounded-lg flex items-center justify-center">
                <Map className="w-4 h-4 text-[#2D5A27]" />
              </span>
              Zones du projet
            </h3>
            
            {masterplanData.zones.map((zone, index) => (
              <ZoneLegendItem
                key={zone.name}
                zone={zone}
                index={index}
                isActive={currentZone === index}
                onMouseEnter={() => setHoveredZone(index)}
                onMouseLeave={() => setHoveredZone(null)}
                onClick={() => setActiveZone(activeZone === index ? null : index)}
              />
            ))}
          </div>

          {/* Plan Image - Static - Center/Right */}
          <div className="lg:col-span-8">
            <div className="bg-[#FAFAFA] rounded-2xl p-4 lg:p-6 border border-gray-100 shadow-lg">
              <div className="rounded-xl overflow-hidden">
                <img
                  src={masterplanData.image}
                  alt="Plan de Morcellement - Les Terres de Songon"
                  className="w-full h-auto"
                />
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-4 gap-4 mt-6">
                {masterplanData.stats.map((stat) => (
                  <div 
                    key={stat.label} 
                    className="bg-white rounded-xl p-4 text-center border border-gray-100 hover:border-[#2D5A27]/30 hover:shadow-md transition-all group"
                  >
                    <div className="text-2xl lg:text-3xl font-light text-[#1a1a1a] group-hover:text-[#2D5A27] transition-colors" 
                         style={{ fontFamily: 'Playfair Display, serif' }}>
                      {stat.value}
                    </div>
                    <div className="text-[#666] text-xs tracking-wide uppercase mt-1">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Masterplan;
